import {
  BundleComponentDto,
  OnsiteUpsellComponentDto,
} from "@intelligems/ig-types/dtos/components";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import {
  ExperienceDto,
  ExperienceTestTypeDto,
} from "@intelligems/ig-types/dtos/experiences";
import { ExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { ExperienceListDto } from "@intelligems/ig-types/dtos/experiences-list";
import { ModKeys } from "@intelligems/ig-types/fe-only/personalizations";
import { OfferDto } from "@intelligems/ig-types/dtos/offers";
import {
  calculateModificationTypes,
  calculateProductCategories,
  extractComponents,
  type VariationWithComponents,
} from "@intelligems/ig-analytics/experience-analytics";
import {
  camelToSnakeCase,
  officialCategoryName,
} from "@intelligems/ig-utils/common";
import { getActiveComponentDiscountTiers } from "@intelligems/ig-utils/component-discount";
import { AnalyticsPageType } from "@src/api/types/dashboards";
import { getSummaryTextFromHTML } from "@src/store/slices/experienceBuilder/common/utils/html/getSummaryTextFromHTML";
import { modSelector } from "@src/store/slices/experienceBuilder/common/utils/modSelector";
import type {
  EmbeddedMod,
  EmbeddedModType,
} from "@src/store/slices/experienceBuilder/types";

// Re-export shared functions for backwards compatibility with existing imports
export {
  calculateModificationTypes,
  calculateProductCategories,
  extractComponents,
} from "@intelligems/ig-analytics/experience-analytics";

export const getProductCategoryFromModKey = (
  modKey: ModKeys,
  category: ExperienceCategory
) => {
  const productCategory = officialCategoryName(category, false);
  let modKeyCopy = "";

  if (modKey === "price") {
    modKeyCopy = "price";
  } else if (modKey === "checkoutBlock") {
    modKeyCopy = "checkout_block";
  } else if (modKey === "postPurchase") {
    modKeyCopy = "post_purchase";
  } else if (modKey === "offer") {
    modKeyCopy = "offer";
  } else if (modKey === "shipping") {
    modKeyCopy = "shipping";
  } else {
    modKeyCopy = "content";
  }
  return `${modKeyCopy}_${productCategory}`;
};

/**
 * Core properties payload shared by experience_saved and visual builder events.
 * Per Loom transcript: "extract out this into a separate function since that
 * seems to be the root of what we actually need"
 */
export type BasePropertiesExperience = {
  category: ExperienceCategory;
  id?: string;
  name: string;
  testTypes: Partial<ExperienceTestTypeDto>;
  variations: readonly VariationWithComponents[];
};

const EMPTY_TEST_TYPES: ExperienceTestTypeDto = {
  hasTestPricing: false,
  hasTestShipping: false,
  hasTestCampaign: false,
  hasTestContent: false,
  hasTestContentUrl: false,
  hasTestContentAdvanced: false,
  hasTestContentOnsite: false,
  hasTestContentTemplate: false,
  hasTestContentTheme: false,
  hasTestOnsiteInjections: false,
  hasTestCheckoutBlocks: false,
  hasTestPostPurchase: false,
  hasTestOnsiteUpsell: false,
};

export const constructBasePropertiesPayload = (
  experience: BasePropertiesExperience
) => {
  const testTypes = { ...EMPTY_TEST_TYPES, ...experience.testTypes };
  const modificationTypes = calculateModificationTypes(testTypes);
  const components = extractComponents(experience.variations);

  return {
    experience_id: experience.id,
    experience_name: experience.name,
    experience_type: officialCategoryName(experience.category, false),
    test_group_count: experience.variations.length,
    modification_types: modificationTypes,
    modification_count: modificationTypes.length,
    components,
    component_count: components.length,
    product_categories: calculateProductCategories({
      ...experience,
      testTypes,
    }),
  };
};

export type MpPropertiesPayloadType = Record<
  string,
  number | string | string[] | undefined
>;

/**
 * Full payload for experience events.
 * Uses constructBasePropertiesPayload as core and adds additional properties.
 * Note: This function uses 'name' for backwards compatibility with existing events.
 */
export const constructBaseMpPayload = (
  experience: Pick<
    ExperienceDto | ExperienceListDto | ExperienceFe,
    | "testTypes"
    | "id"
    | "name"
    | "category"
    | "variations"
    | "productVariantCount"
  >,
  offers: OfferDto[]
) => {
  const {
    experience_id,
    experience_name,
    experience_type,
    product_categories,
    modification_types,
    modification_count,
    components,
    component_count,
  } = constructBasePropertiesPayload(experience);

  // Use 'name' instead of 'experience_name' for backwards compatibility
  const properties: MpPropertiesPayloadType = {
    experience_id,
    name: experience_name, // Keep 'name' for existing events
    experience_type,
    product_categories,
    test_group_count: experience.variations.length,
    modification_types,
    modification_count,
    components,
    component_count,
  };

  if (experience.testTypes.hasTestCampaign) {
    const discountTypes: string[] = [];
    const applicationTypes: string[] = [];

    offers.forEach((offer) => {
      discountTypes.push(camelToSnakeCase(offer.discountType));
      applicationTypes.push(camelToSnakeCase(offer.discountApplicationType));
    });

    if (discountTypes.length > 0) {
      properties.discount_types = discountTypes;
    }
    if (applicationTypes.length > 0) {
      properties.application_types = applicationTypes;
    }
  }

  if (experience.testTypes.hasTestPricing && experience.productVariantCount) {
    properties.product_count = experience.productVariantCount;
  }

  return properties;
};

/**
 * The onsite-upsell tracking properties derived from a saved component, shared
 * by the creation_completed and edited events so both read the same shape.
 * `has_discount` mirrors the SavedUpsell view's "no discount" rule.
 */
export const getOnsiteUpsellMpProperties = (
  component: OnsiteUpsellComponentDto | BundleComponentDto
) => ({
  componentId: component.id,
  surface: component.config.placement.location,
  hasDiscount: getActiveComponentDiscountTiers(component.config) !== null,
  upsellType:
    component.config.type === "bundleOnsiteUpsell"
      ? ("bundle" as const)
      : ("simple" as const),
});

export const getAnalyticsPageType = (
  category: ExperienceCategory
): AnalyticsPageType =>
  category === "personalization"
    ? "personalization_analytics"
    : "experiment_analytics";

/**
 * Payload for modification-specific visual builder events.
 * Used for: element_selected, mod_saved, mod_edit_started, mod_edit_cancelled, mod_deleted
 */
export type ModificationEditType =
  | "replacement"
  | "hide"
  | "find_and_replace"
  | "html";

export type ModificationElementType = EmbeddedModType | "css" | "js";

export interface ModificationPayload {
  selector: string;
  elementType: ModificationElementType;
  originalText?: string;
  elementDescription?: string;
  editType?: ModificationEditType;
  imageReplaceAll?: boolean;
  groupName: string;
}

export const constructModificationPayload = (
  mod: EmbeddedMod,
  variationName: string
): ModificationPayload => {
  // Handle injection mods differently - they don't have selector
  if (mod.type === "injection") {
    return {
      selector: "",
      elementType: mod.tab === "css" ? "css" : "js",
      groupName: variationName,
    };
  }

  const base: ModificationPayload = {
    // Empty for an anchored upsell: the merchant's theme block owns the
    // position, so there is no selector to report.
    selector: modSelector(mod) ?? "",
    elementType: mod.type,
    groupName: variationName,
  };

  if (mod.type === "text") {
    base.originalText = getSummaryTextFromHTML(mod.nodes?.[0]?.html);
    base.elementDescription = mod.description;

    if (mod.remove) {
      base.editType = "hide";
    } else if (mod.editMode === "html") {
      base.editType = "html";
    } else if (mod.find) {
      base.editType = "find_and_replace";
    } else {
      base.editType = "replacement";
    }
  } else if (mod.type === "image") {
    base.elementDescription = mod.description;

    if (mod.remove) {
      base.editType = "hide";
    } else if (mod.editMode === "html") {
      base.editType = "html";
    } else {
      base.editType = "replacement";
    }
    // imageReplaceAll: true if "All instances of this image" is selected
    base.imageReplaceAll = !!mod.imageSearchUrl;
  }

  return base;
};
